import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { Button, Progress } from "reactstrap";
import { ProductContext } from "../../../store";
import { PostData } from "../../../services/FetchData";
import { toast } from "react-toastify";

const POLL_MS = 2000;

const CustomProductPageCrawl = () => {
	const { currentWebsiteId, websitePlatform } = useContext(ProductContext);
	const [jobId, setJobId] = useState(null);
	const [crawlGroupId, setCrawlGroupId] = useState(null);
	const [processed, setProcessed] = useState(0);
	const [total, setTotal] = useState(0);
	const [running, setRunning] = useState(false);
	const toastShownRef = useRef(false);
	const lastTotalRef = useRef(0);

	const fetchStatus = useCallback(
		async (jid) => {
			if (!currentWebsiteId || (!jid && !crawlGroupId)) return;
			try {
				const res = await PostData("ms1", "api/v1/aiassistant/customProductPageCrawl/status", {
					...(jid ? { jobId: jid } : {}),
					websiteId: currentWebsiteId,
					...(crawlGroupId ? { crawlGroupId } : {}),
				});
				if (res === "Invalid" || !res) return;
				if (res.status === "success" && res.data) {
					const d = res.data;
					if (d.gone) {
						setRunning(false);
						setCrawlGroupId(null);
						if (lastTotalRef.current > 0) {
							setProcessed(lastTotalRef.current);
							setTotal(lastTotalRef.current);
						}
						if (!toastShownRef.current) {
							toastShownRef.current = true;
							toast.success("Product page crawl finished");
						}
						return;
					}
					const proc = typeof d.processed === "number" ? d.processed : 0;
					const tot = typeof d.total === "number" ? d.total : 0;
					lastTotalRef.current = tot || lastTotalRef.current;
					setProcessed(proc);
					setTotal(tot);
					const active = d.runningStatus === "Pending" || d.runningStatus === "Started";
					if (!active && tot > 0 && proc >= tot) {
						setRunning(false);
						setCrawlGroupId(null);
						if (!toastShownRef.current) {
							toastShownRef.current = true;
							toast.success("Product page crawl finished");
						}
					}
				} else if (res.status === "failure") {
					setRunning(false);
					setCrawlGroupId(null);
				}
			} catch (e) {
				console.error(e);
			}
		},
		[currentWebsiteId, crawlGroupId]
	);

	useEffect(() => {
		if (!currentWebsiteId || websitePlatform !== "custom") return;
		let cancelled = false;
		(async () => {
			try {
				const res = await PostData("ms1", "api/v1/aiassistant/customProductPageCrawl/active", {
					websiteId: currentWebsiteId,
				});
				if (cancelled || res === "Invalid" || !res || res.status !== "success") return;
				const d = res.data;
				if (d?.active && d.jobId) {
					setJobId(d.jobId);
					if (d.crawlGroupId) setCrawlGroupId(d.crawlGroupId);
					setProcessed(typeof d.processed === "number" ? d.processed : 0);
					setTotal(typeof d.total === "number" ? d.total : 0);
					lastTotalRef.current = d.total || 0;
					setRunning(true);
					toastShownRef.current = false;
				}
			} catch (e) {
				console.error(e);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [currentWebsiteId, websitePlatform]);

	useEffect(() => {
		if (!running || (!jobId && !crawlGroupId)) return;
		const id = setInterval(() => fetchStatus(jobId), POLL_MS);
		return () => clearInterval(id);
	}, [running, jobId, crawlGroupId, fetchStatus]);

	useEffect(() => {
		if (running && (jobId || crawlGroupId)) fetchStatus(jobId);
	}, [running, jobId, crawlGroupId, fetchStatus]);

	const startCrawl = async () => {
		if (!currentWebsiteId) {
			toast.error("Select a website");
			return;
		}
		toastShownRef.current = false;
		try {
			const res = await PostData("ms1", "api/v1/aiassistant/customProductPageCrawl/start", {
				websiteId: currentWebsiteId,
			});
			if (res === "Invalid" || !res) {
				toast.error("Could not start crawl");
				return;
			}
			if (res.status === "failure") {
				toast.error(res.message || "Could not start crawl");
				return;
			}
			if (res.status === "success" && res.data?.jobId) {
				setJobId(res.data.jobId);
				setCrawlGroupId(res.data.crawlGroupId ?? null);
				setTotal(res.data.total ?? 0);
				lastTotalRef.current = res.data.total ?? 0;
				setProcessed(0);
				setRunning(true);
				toast.success("Crawl started");
			}
		} catch (e) {
			toast.error("Failed to start crawl");
		}
	};

	const stopCrawl = async () => {
		if (!jobId && !crawlGroupId) return;
		if (!currentWebsiteId) return;
		try {
			const res = await PostData("ms1", "api/v1/aiassistant/customProductPageCrawl/cancel", {
				...(jobId ? { jobId } : {}),
				websiteId: currentWebsiteId,
				...(crawlGroupId ? { crawlGroupId } : {}),
			});
			if (res?.status === "success") {
				toast.info("Stopping after the current batch…");
			}
		} catch (e) {
			toast.error("Cancel failed");
		}
	};

	const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

	if (websitePlatform !== "custom") {
		return (
			<div className="p-4">
				<p className="text-muted mb-0">Product page crawl is only available for custom platform stores.</p>
			</div>
		);
	}

	return (
		<div className="p-4">
			<h4 className="mb-2">Product page crawl</h4>
			<p className="text-muted">
				Fetches each indexed product page, extracts main content as Markdown and collects PDF links for your AI
				assistant index.
			</p>
			<div className="mb-3">
				<Button color="primary" onClick={startCrawl} disabled={running || !currentWebsiteId}>
					Crawl product pages
				</Button>{" "}
				<Button color="secondary" outline onClick={stopCrawl} disabled={!running}>
					Stop
				</Button>
			</div>
			{(running || (total > 0 && processed >= total)) && (
				<div style={{ maxWidth: 480 }}>
					<div className="small text-muted mb-1">
						{processed} / {total} products
					</div>
					<Progress value={pct} />
				</div>
			)}
		</div>
	);
};

export default CustomProductPageCrawl;
